import apiClient from './apiService';

export interface GeographyType {
    id: number;
    entity_id: number;
    name: string;
    description: string | null;
    is_active: number;
    code?: string | null;
    code_seq?: number | null;
    // Workflow
    workflow_state?: string;
    is_approved?: number;
    submitted_by?: number | null;
    submitted_on?: string | null;
    approved_by?: number | null;
    approved_on?: string | null;
    approval_note?: string | null;
    rejected_by?: number | null;
    rejected_on?: string | null;
    rejection_reason?: string | null;
    created_by?: number | null;
    created_on?: string | null;
    modified_by?: number | null;
    modified_on?: string | null;
}

export interface GeographyTypeListResponse {
    success: boolean;
    data: GeographyType[];
    meta: { page: number; limit: number; total: number; pages: number };
}

export const geographyTypeService = {
    list: async (
        params?: { search?: string; status?: 'ALL' | 'ACTIVE' | 'INACTIVE'; page?: number; limit?: number }
    ): Promise<GeographyTypeListResponse> => {
        const qs = new URLSearchParams();
        if (params?.search) qs.append('search', params.search);
        if (params?.status) qs.append('status', params.status);
        if (params?.page) qs.append('page', String(params.page));
        if (params?.limit) qs.append('limit', String(params.limit));
        const url = `/api/v1/org/geography-types${qs.toString() ? `?${qs}` : ''}`;
        const resp = await apiClient.get<GeographyTypeListResponse>(url);
        return resp.data;
    },

    dropdown: async (): Promise<GeographyType[]> => {
        const resp = await apiClient.get<{ items: GeographyType[] }>('/api/v1/org/geography-types/dropdown');
        return resp.data.items || [];
    },

    create: async (data: { name: string; description?: string }): Promise<GeographyType> => {
        const resp = await apiClient.post<GeographyType>('/api/v1/org/geography-types', data);
        return resp.data;
    },

    update: async (
        id: number,
        data: Partial<{ name: string; description: string; is_active: number }>,
    ): Promise<GeographyType> => {
        const resp = await apiClient.put<GeographyType>(`/api/v1/org/geography-types/${id}`, data);
        return resp.data;
    },

    remove: async (id: number): Promise<void> => {
        await apiClient.delete(`/api/v1/org/geography-types/${id}`);
    },

    submit: async (id: number): Promise<void> => {
        await apiClient.post(`/api/v1/org/geography-types/${id}/submit`);
    },

    approve: async (id: number, note?: string): Promise<void> => {
        await apiClient.post(`/api/v1/org/geography-types/${id}/approve`, { note });
    },

    reject: async (id: number, reason: string): Promise<void> => {
        await apiClient.post(`/api/v1/org/geography-types/${id}/reject`, { reason });
    },

    reopen: async (id: number): Promise<void> => {
        await apiClient.post(`/api/v1/org/geography-types/${id}/reopen`);
    },

    getHistory: async (id: number): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> => {
        const resp = await apiClient.get<{ success: boolean; data: any[] }>(`/api/v1/org/geography-types/${id}/history`);
        return (resp.data.data || []).map((r: any) => ({
            audit_id: r.id,
            action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
            changed_by_username: r.changed_by_username || 'Unknown',
            changed_on: r.changed_on,
            change_reason: r.remarks,
        }));
    },
};
